import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';
import { environment } from '../../../../../environments';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { User } from '../../../../core/interfaces/user.interface';
import { AppState } from '../../../../store/app.state';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { selectAllSubAccounts } from '../../../../store/sub-accounts/sub-accounts.selectors';
import * as SubAccountsActions from '../../../../store/sub-accounts/sub-accounts.actions';
import { IdentityCardService } from '../../../../core/services/identity-card.service';
import { DocumentService } from '../../../../core/services/document.service';
import { ToastService } from '../../../../core/services/toast.service';
import { switchMap, mergeMap, from } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_AUTH } from '../../../../core/interceptors/auth.interceptor';
import {
  PatientCardComponent,
  PatientCardModel,
} from './patient-card/patient-card.component';

type HealthCardViewModel = PatientCardModel;

@Component({
  selector: 'app-passport-card',
  standalone: true,
  imports: [CommonModule, BannerComponent, PatientCardComponent],
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
})
export class PassportCardComponent implements OnInit, OnDestroy {
  isDppTheme = environment.theme.default === 'dpp';
  selectedCountry: Country | null = null;
  healthCards: HealthCardViewModel[] = [];
  private currentUser: User | null = null;
  private subAccounts: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    birthday?: string;
    passportNumber?: string;
  }> = [];
  private subscription = new Subscription();
  links = [
    { label: 'Votre passeport', href: '/passport' },
    { label: 'Votre carte de santé', href: undefined },
  ];

  constructor(
    private store: Store<AppState>,
    private identityCardService: IdentityCardService,
    private documentService: DocumentService,
    private http: HttpClient,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
          this.updatePassportLink();
        } catch {
          this.selectedCountry = null;
        }
      }
    }

    this.store.dispatch(SubAccountsActions.loadSubAccounts());

    this.subscription.add(
      this.store.select(selectCurrentUser).subscribe((user) => {
        this.currentUser = user;
        this.buildHealthCards();
      })
    );

    this.subscription.add(
      this.store.select(selectAllSubAccounts).subscribe((subAccounts) => {
        this.subAccounts = subAccounts || [];
        this.buildHealthCards();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  generateQrCode(card: HealthCardViewModel): void {
    if (card.generating) {
      return;
    }

    card.generating = true;
    const passportNumber = card.passportNumber;
    this.identityCardService.generateIdentityCard(passportNumber).pipe(
      switchMap((doc) => this.documentService.getPresignedUrl(doc.url)),
      mergeMap(({ url }) => this.http.get(url, {
        responseType: 'blob',
        context: new HttpContext().set(SKIP_AUTH, true),
      })),
      mergeMap((blob) => from(this.blobToDataUrl(blob)))
    ).subscribe({
      next: () => {
        const current = this.healthCards.find((c) => c.passportNumber === passportNumber);
        if (!current) return;
        current.generating = false;
        this.toastService.success('Code QR généré avec succès');
        this.loadCardDocument(current);
      },
      error: () => {
        const current = this.healthCards.find((c) => c.passportNumber === passportNumber);
        if (current) current.generating = false;
        this.toastService.error('Impossible de générer le code QR');
      },
    });
  }

  private updatePassportLink(): void {
    if (this.selectedCountry?.code) {
      this.links[0].href = `/${this.selectedCountry.code}/passport`;
    }
  }

  private buildHealthCards(): void {
    const previousCardsByPassport = new Map(
      this.healthCards.map((card) => [card.passportNumber, card])
    );
    const cards: HealthCardViewModel[] = [];

    if (this.currentUser?.passportNumber) {
      cards.push(
        this.createOrReuseCard(
          this.currentUser._id,
          this.currentUser.firstName,
          this.currentUser.lastName,
          this.currentUser.birthday ?? null,
          this.currentUser.passportNumber,
          'Titulaire principal',
          previousCardsByPassport
        )
      );
    }

    this.subAccounts
      .filter((subAccount) => !!subAccount.passportNumber)
      .forEach((subAccount) => {
        cards.push(
          this.createOrReuseCard(
            subAccount._id,
            subAccount.firstName,
            subAccount.lastName,
            subAccount.birthday ?? null,
            subAccount.passportNumber as string,
            'Sous-compte',
            previousCardsByPassport
          )
        );
      });

    this.healthCards = cards;
    this.healthCards.forEach((card) => {
      if (!card.hasDocument && !card.loading) {
        this.loadCardDocument(card);
      }
    });
  }

  private createOrReuseCard(
    id: string,
    firstName: string,
    lastName: string,
    birthday: string | null,
    passportNumber: string,
    relationLabel: string,
    previousCardsByPassport: Map<string, HealthCardViewModel>
  ): HealthCardViewModel {
    const previous = previousCardsByPassport.get(passportNumber);
    if (!previous) {
      return {
        id,
        fullname: `${firstName} ${lastName}`.trim(),
        firstName,
        lastName,
        birthday,
        passportNumber,
        relationLabel,
        loading: false,
        hasDocument: false,
        generating: false,
        documentUrl: null,
      };
    }

    previousCardsByPassport.delete(passportNumber);
    return {
      ...previous,
      id,
      fullname: `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      birthday,
      relationLabel,
    };
  }

  private loadCardDocument(card: HealthCardViewModel): void {
    card.loading = true;
    const passportNumber = card.passportNumber;
    this.identityCardService.getIdentityCardDocument(passportNumber).pipe(
      switchMap((doc) => this.documentService.getPresignedUrl(doc.url)),
      mergeMap(({ url }) => this.http.get(url, {
        responseType: 'blob',
        context: new HttpContext().set(SKIP_AUTH, true),
      })),
      mergeMap((blob) => from(this.blobToDataUrl(blob)))
    ).subscribe({
      next: (dataUrl) => {
        const current = this.healthCards.find((c) => c.passportNumber === passportNumber);
        if (!current) return;
        current.documentUrl = dataUrl;
        current.hasDocument = true;
        current.loading = false;
      },
      error: () => {
        const current = this.healthCards.find((c) => c.passportNumber === passportNumber);
        if (!current) return;
        current.documentUrl = null;
        current.hasDocument = false;
        current.loading = false;
      },
    });
  }

}
